// components/forms/AgentInternalForm.js
import React, { useState, useEffect } from "react";
import { createAgent, getAllAgents, updateAgent, searchAgent } from "../../services/AgentService.js";

function AgentInternalForm({ onCancel, initialData }) {
  const [formData, setFormData] = useState({
    agentName: "",
    agentCode: "",
    email: "",
    phone: "",
    address: "",
    commissionRate: "",
    status: "active",
    agreementRenewed: "No",
    agreementRenewedDate: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        agentName: initialData.agentName || "",
        agentCode: initialData.agentCode || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        address: initialData.address || "",
        commissionRate: initialData.commissionRate || "",
        status: initialData.status || "active",
        agreementRenewed: initialData.agreementRenewed || "No",
        agreementRenewedDate: initialData.agreementRenewedDate || "",
        notes: initialData.notes || "",
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Double-check with backend before saving
      const allAgents = await searchAgent(formData.agentCode);
      const agents = Array.isArray(allAgents)
        ? allAgents
        : allAgents?.data || [];
      // Inside handleSubmit before saving
      const newAgentCode = formData.agentCode?.trim();

      if (newAgentCode && newAgentCode !== "-") {
        const duplicateAgent = agents.find(
          (agent) =>
            agent.id !== initialData?.id && // ignore current record when editing
            agent.agentCode?.trim().toLowerCase() === newAgentCode.toLowerCase()
        );

        if (duplicateAgent) {
          alert(`Agent Code "${newAgentCode}" already exists.`);
          return; // stop submission
        }
      }
      if (initialData?.id) {
        await updateAgent(initialData.id, formData);
      } else {
        const newId = await createAgent(formData);
      }
      alert("Agent record saved successfully!");
      if (onCancel) onCancel();
    } catch (error) {
      alert("Failed to save agent record. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "12px"
          }}
        >
          <div className="spinner" style={{ textAlign: "center" }}>
            <div className="loader" style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 12px",
              
            }} />
            
          </div>
          <p style={ {
            padding: "0.5rem",
          }
          }>Submitting, please wait!</p>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        {/* Actions + Title */}
        <div
          className="actions"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "1rem",
          }}
        >
          <div>
            <h1 className="page-title">Agent Record</h1>
            <p className="page-subtitle">Create and manage Agents</p>
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        </div>

        {/* Agent Information */}
        <div className="form-section">
          <h2>Agent Information</h2>
          <br />
          <div className="form-grid">
            {[
              { label: "Agent Name", name: "agentName" },
              { label: "Agent Code", name: "agentCode", placeholder: "e.g., AG001" },
              { label: "Email", name: "email", type: "email" },
              { label: "Phone", name: "phone", type: "tel" },
              {
                label: "Commission Rate (%)",
                name: "commissionRate",
                type: "number",
                placeholder: "e.g., 5.5",
              },
            ].map(({ label, name, type = "text", placeholder }) => (
              <div key={name} className="form-group">
                <label>{label}</label>
                <input
                  type={type}
                  name={name}
                  value={formData[name]}
                  placeholder={placeholder || ""}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            ))}

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="new">New</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
                
              </select>
            </div>
            <div className="form-group">
              <label>Agreement Renewed</label>
              <select
                name="agreementRenewed"
                value={formData.agreementRenewed}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </div>

            <div className="form-group">
              <label>Agreement Renewed Date</label>
              <input
                type="date"
                name="agreementRenewedDate"
                value={formData.agreementRenewedDate}
                onChange={handleInputChange}
                className="form-input"
                disabled={formData.agreementRenewed !== "Yes"}
                required={formData.agreementRenewed === "Yes"}
              />
            </div>
          </div>
          
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h2>Additional Information</h2>
          <br />
          <div className="form-grid">
            <div className="form-group">
              <label>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="form-textarea"
                rows="3"
                placeholder="Enter agent's full address..."
                required
              />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-textarea"
                rows="3"
                placeholder="Any additional notes about the agent..."
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AgentInternalForm;
